import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { throwIfError, useApiClient as createApiClient } from '@vrplatform/api';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as z from 'zod';
import {
  ApiClientProvider,
  showApiErrorNotification,
  useApiClient,
  useApiEndpoint,
} from '../../api';
import { DirectTokenGtqyProvider } from '../../graphql/react-query';
import { getActiveOwnerPortal } from '../../owner-portal';
import { DirectTokenTrpcProvider } from '../../trpc';
import {
  OwnerPortalContactContextProvider,
  ownerPortalPreviewSessionContext,
} from '../owner';
import { DirectTokenSessionProvider } from './team.direct-token';
import { useSessionTokenProvider } from './team.session';

export type OwnerPortalPreviewTarget = {
  teamId: string;
  contactId: string;
};

export type OwnerPortalPreviewRequestStatus =
  | { status: 'loading' }
  | { status: 'failed'; error: Error };

export type OwnerPortalPreviewExpiredStatus = {
  restart: () => void;
  exit: () => void;
};

const ownerPortalPreviewSchema = z.object({
  accessToken: z.string().min(1),
  contactId: z.uuid(),
  expiresAt: z.iso.datetime({ offset: true }),
  restartUrl: z.url(),
  teamId: z.uuid(),
  userId: z.uuid(),
});
export type OwnerPortalPreview = z.infer<typeof ownerPortalPreviewSchema>;

// Per tab: the preview survives reloads of its own tab, but never leaks into
// another tab on the same host.
const OWNER_PORTAL_PREVIEW_STORAGE_KEY = 'vrp_owner_portal_preview';

export function clearOwnerPortalPreview() {
  sessionStorage.removeItem(OWNER_PORTAL_PREVIEW_STORAGE_KEY);
}

export function readOwnerPortalPreview(): OwnerPortalPreview | null {
  const value = sessionStorage.getItem(OWNER_PORTAL_PREVIEW_STORAGE_KEY);
  if (!value) return null;

  const preview = ownerPortalPreviewSchema.safeParse(JSON.parse(value));
  if (!preview.success) {
    // Cleared first so a reload returns this tab to its normal session.
    clearOwnerPortalPreview();
    throw new Error('Stored owner portal preview is invalid');
  }

  return preview.data;
}

function OwnerPortalPreviewRequest({
  renderStatus,
  target,
}: {
  renderStatus: (status: OwnerPortalPreviewRequestStatus) => ReactNode;
  target: OwnerPortalPreviewTarget;
}) {
  const api = useApiClient();
  const start = useQuery({
    queryKey: ['ownerPortalPreviewStart', target.teamId, target.contactId],
    queryFn: async () =>
      throwIfError(
        api.POST('/contacts/{id}/preview', {
          params: { path: { id: target.contactId } },
        })
      ),
    gcTime: 0,
    retry: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // The preview opens where the owner signs in, which can be the team's
    // custom domain.
    if (start.data) window.location.replace(start.data.url);
  }, [start.data]);

  return renderStatus(
    start.error
      ? { status: 'failed', error: start.error }
      : { status: 'loading' }
  );
}

/**
 * Starts a preview with the manager's Clerk session and sends the tab to the
 * single-use address the API returns.
 */
export function OwnerPortalPreviewStart({
  renderStatus,
  target,
}: {
  renderStatus: (status: OwnerPortalPreviewRequestStatus) => ReactNode;
  target: OwnerPortalPreviewTarget;
}) {
  const { getToken, refreshToken } = useSessionTokenProvider();
  const [queryClient] = useState(() => new QueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <ApiClientProvider
        attempts={1}
        getToken={getToken}
        isTeamIdFetching={false}
        refreshToken={refreshToken}
        teamId={target.teamId}
      >
        <OwnerPortalPreviewRequest
          renderStatus={renderStatus}
          target={target}
        />
      </ApiClientProvider>
    </QueryClientProvider>
  );
}

/**
 * Exchanges the single-use code from the address fragment for the preview token,
 * keeps it in this tab, and reloads the tab into the preview. Needs no login,
 * so it works on custom domains.
 */
export function OwnerPortalPreviewOpen({
  renderStatus,
}: {
  renderStatus: (status: OwnerPortalPreviewRequestStatus) => ReactNode;
}) {
  const { baseUrl } = useApiEndpoint();
  // Read once from the fragment, then removed from the address bar and
  // history.
  const [code] = useState(() =>
    new URLSearchParams(window.location.hash.slice(1)).get('code')
  );
  const [queryClient] = useState(() => new QueryClient());

  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const exchange = useQuery(
    {
      queryKey: ['ownerPortalPreviewExchange', baseUrl, code],
      queryFn: async () => {
        if (!code) throw new Error('Owner portal preview link is invalid');
        const portal = getActiveOwnerPortal();
        return throwIfError(
          createApiClient({ attempts: 1, baseUrl }).POST(
            '/owner-portal-previews/exchange',
            {
              body: { code },
              headers: portal ? { 'x-owner-portal': portal.hostname } : {},
              cache: 'no-store',
            }
          )
        );
      },
      gcTime: 0,
      retry: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
    queryClient
  );

  useEffect(() => {
    if (!exchange.data) return;
    sessionStorage.setItem(
      OWNER_PORTAL_PREVIEW_STORAGE_KEY,
      JSON.stringify(exchange.data)
    );
    window.location.replace('/');
  }, [exchange.data]);

  return renderStatus(
    exchange.error
      ? { status: 'failed', error: exchange.error }
      : { status: 'loading' }
  );
}

function OwnerPortalPreviewSessionContent({
  children,
  contactId,
  onEnded,
  teamId,
}: {
  children: ReactNode;
  contactId: string;
  onEnded: () => void;
  teamId: string;
}) {
  // The preview's own client, so ending sends its token and portal header.
  const api = useApiClient();
  const previewSession = useMemo(
    () => ({
      contactId,
      teamId,
      // Ending the session revokes the token before the tab closes.
      exit: () => {
        throwIfError(api.POST('/owner-portal-previews/end')).then(
          onEnded,
          (error) =>
            showApiErrorNotification({
              error,
              title: 'Could not exit the preview',
            })
        );
      },
    }),
    [api, contactId, onEnded, teamId]
  );

  return (
    <ownerPortalPreviewSessionContext.Provider value={previewSession}>
      {children}
    </ownerPortalPreviewSessionContext.Provider>
  );
}

/**
 * Renders the owner portal for one owner contact with the stored preview
 * token. Every REST, Hasura, and tRPC request below runs with that token.
 */
export function OwnerPortalPreviewSessionProvider({
  children,
  preview,
  renderExpired,
}: {
  children: ReactNode;
  preview: OwnerPortalPreview;
  renderExpired: (status: OwnerPortalPreviewExpiredStatus) => ReactNode;
}) {
  // Preview data never shares a cache with another session.
  const [queryClient] = useState(
    () => new QueryClient({ defaultOptions: { queries: { retry: false } } })
  );
  const [isExpired, setIsExpired] = useState(false);
  const { accessToken, contactId, expiresAt, restartUrl, teamId, userId } =
    preview;

  useEffect(() => {
    const timeout = setTimeout(
      () => setIsExpired(true),
      Math.max(0, Date.parse(expiresAt) - Date.now())
    );

    return () => clearTimeout(timeout);
  }, [expiresAt]);

  const close = useCallback(() => {
    clearOwnerPortalPreview();
    // The preview tab was opened by the manager app; when the browser keeps
    // it open, it returns to the host that started the preview.
    window.close();
    window.location.assign(new URL('/', restartUrl));
  }, [restartUrl]);

  const session = useMemo(() => ({ teamId, userId }), [teamId, userId]);

  if (isExpired)
    return renderExpired({
      restart: () => {
        clearOwnerPortalPreview();
        window.location.assign(restartUrl);
      },
      exit: close,
    });

  return (
    <QueryClientProvider client={queryClient}>
      <DirectTokenTrpcProvider
        accessToken={accessToken}
        queryClient={queryClient}
        url="/trpc"
      >
        <DirectTokenGtqyProvider accessToken={accessToken}>
          <DirectTokenSessionProvider
            accessToken={accessToken}
            onAuthenticatedRequestFailure={(status) => {
              // Writes are rejected with 403 by design; only a 401 means the
              // token expired or was revoked.
              if (status !== 401) return false;
              setIsExpired(true);
              return true;
            }}
            session={session}
          >
            <OwnerPortalContactContextProvider>
              <OwnerPortalPreviewSessionContent
                contactId={contactId}
                onEnded={close}
                teamId={teamId}
              >
                {children}
              </OwnerPortalPreviewSessionContent>
            </OwnerPortalContactContextProvider>
          </DirectTokenSessionProvider>
        </DirectTokenGtqyProvider>
      </DirectTokenTrpcProvider>
    </QueryClientProvider>
  );
}
